import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { catchError, combineLatest, distinctUntilChanged, filter, map, Observable, of, startWith, switchMap, take, throwError } from 'rxjs';
import { AuthService, ContactUser } from '@aiwyn/auth';
import { ENVIRONMENT_VARIABLES, EnvironmentVariables, ignoreFalsy } from '@aiwyn/shared';
// The read path (`getRequestDetail` / `getItemDetail`) goes through the real CRL
// REST endpoints (`CxpCrlRestApiService`). The write methods still reuse the
// APP-LOCAL `ClientRequestApiService` until dedicated CRL write endpoints land.
//
// NOTE on the write methods below: these reuse the APP-LOCAL `ClientRequestApiService`
// (inline-`gql` Apollo wrapper under `@aiwyn/api`) — NOT the legacy NGXS
// `ClientRequestsState`/`CommonClientRequestsState` modules. Per cxp-architecture,
// only services + state handlers may touch the domain/API layer, so all of it is
// funnelled through this bridge.
//
// Contact identities (right-rail + panel assignee names/emails) now arrive
// resolved on the `/page` read itself — the backend joins contactInfo and
// returns fullName + email on both the request-level and item-level assignees —
// so there is no separate contact-directory lookup here anymore.
import {
  ClientRequestApiService,
  ClientRequestItemActivity,
  ClientRequestItemActivityActor,
  ClientRequestItemActivityResponse,
  ClientRequestItemStatus,
  InviteAssignee,
  MASKED_ITEM_RESPONSE_VALUE,
  RequestFileCategorization,
  RequestItemCommentRef,
  RequestItemType,
  RequestSubmissionFileStatus,
} from '@aiwyn/api';
import { AppState } from '../../../state/app.state';
// Reused from the legacy client-requests module: strips the upload timestamp
// suffix (`_<epoch>`) from a stored file name for display.
import { getOriginalBoxFileName } from '../../client-requests/models';
import {
  CRL_DONE_STATUSES,
  CRL_SUBMITTED_ITEM_STATUS,
  CrlActivityComment,
  CrlActivityEntry,
  CrlActivityFile,
  CrlActivityLinkKind,
  CrlAssigneeKind,
  CrlAvatarColorKey,
  CrlCategorizationFile,
  CrlCategorizationStatus,
  CrlItemCommentIds,
  CrlItemFile,
  CrlItemStatus,
  CrlItemType,
  CrlRequestDetail,
  CrlRequestItem,
  CrlRequestSection,
} from '../models';
import { pickAvatarColorKey } from '../shared/utils/avatar-color.util';
import { getCxpContactInitials } from '../shared/utils/contact-initials.util';
import {
  CrlApiAssigneeType,
  CrlItemDetailResponse,
  CrlPageItem,
  CrlPageResponse,
  CrlPageSection,
  CxpCrlRestApiService,
} from './cxp-crl-rest-api.service';

/**
 * Single swappable source of truth for raw-status -> display-label mapping in
 * the new CRL design. Co-located with the bridge so it moves/dies with it.
 */
export const CRL_STATUS_LABELS: Record<CrlItemStatus, string> = {
  [CrlItemStatus.WAITING]: 'Waiting for You',
  [CrlItemStatus.IN_REVIEW]: 'In Review',
  [CrlItemStatus.NEEDS_UPDATE]: 'Needs Update',
  [CrlItemStatus.FINISHED]: 'Finished',
  [CrlItemStatus.NOT_APPLICABLE]: 'Marked as N/A',
  [CrlItemStatus.OVERDUE]: 'Overdue',
};

/**
 * Bridge between the new CRL detail page and the data layer. The read path
 * (`getRequestDetail` → `/page`, `getItemDetail` → `/item/detail`) maps the real
 * CRL REST responses into the CXP-local `CrlRequestDetail` / `CrlItemFile`
 * shapes, so the state, selectors, and UI stay agnostic of the wire format. The
 * write methods still reuse the app-local `ClientRequestApiService`.
 */
@Injectable({ providedIn: 'root' })
export class ClientRequestServiceApiBridge {
  private readonly clientRequestApi = inject(ClientRequestApiService);
  private readonly restApi = inject(CxpCrlRestApiService);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store);
  // Typed accessor for the runtime firebase project id — the same
  // `ENVIRONMENT_VARIABLES` token the rest of the app injects (see
  // `src/environments/index.ts`). Gates the dev-only activity-mock injection below.
  private readonly env = inject<EnvironmentVariables>(ENVIRONMENT_VARIABLES);

  /**
   * Fetches a single request's page: the request-header fields the CRL-page
   * selector needs plus every section/item with a normalized status + label,
   * flags, `restricted`, and a `fileCount`. Files and answers are NOT included —
   * each item's `files` is `[]` and `answer` is `undefined` until the overlay
   * lazily loads them via {@link getItemDetail}.
   */
  getRequestDetail(requestId: number): Observable<CrlRequestDetail> {
    return this.restApi.getCrlPage(requestId).pipe(map(mapCrlPageToCrlDetail));
  }

  /**
   * Lazily fetches ONE item's files + answer (the overlay open + post-reload
   * re-hydrate). `files[]` map to client uploads (`isReference:false`), active
   * `fileReferences[]` to firm reference files (`isReference:true`). The answer
   * is masked-aware: a null answer on masked content resolves to the masked
   * marker + `isAnswerMasked:true`. The state handler patches the result onto the
   * matching item inside `detailData`.
   */
  getItemDetail(clientRequestId: number, itemId: number): Observable<CrlItemDetailResult> {
    return this.restApi.getCrlItemDetail(clientRequestId, itemId).pipe(map(mapCrlItemDetail));
  }

  /**
   * Fetches the per-item activity feed and maps it into the CRL feed view model.
   * REAL wire: the app-local `ClientRequestApiService.getClientRequestItemActivity`
   * REST endpoint (`GET …/section/item/{itemId}/activity`). Resolves the current
   * user's contact ids first (like `getRequestDetail`) so the mapper can do
   * best-effort "You" detection; `itemName` (the selected item's name, passed by
   * the state handler) supplies the teal linked object for SENT/APPROVED.
   *
   * Full-load today: a single large page (`limit` = backend max 100, `offset` 0),
   * but the params stay plumbed through so pagination is a drop-in later.
   */
  getItemActivity(itemId: number, itemName: string | null, limit = 100, offset = 0): Observable<CrlActivityEntry[]> {
    return this.authService.getUser().pipe(
      ignoreFalsy(),
      take(1),
      switchMap((user) => {
        const selfContactIds = this.resolveContactIds(user as ContactUser);
        const projectId = this.env.firebaseConfig?.projectId;
        return this.clientRequestApi.getClientRequestItemActivity(itemId, limit, offset).pipe(
          // PERMANENT dev-only tool (see `injectDevActivityMocks`): merges any
          // localStorage-supplied mock activities into the real feed. A complete
          // no-op off `dev-mvp` or without the opt-in key, so it ships safely.
          map((response) => injectDevActivityMocks(response, projectId)),
          // Error recovery: on `dev-mvp` WITH a stored mock, render the mocks into
          // an empty envelope so a down/empty event-engine env still shows a feed.
          // Otherwise the real failure propagates unchanged.
          catchError((error: unknown) => {
            const recovered = injectDevActivityMocks(null, projectId);
            return recovered && recovered.activities.length > 0 ? of(recovered) : throwError(() => error);
          }),
          map((response) => mapItemActivityToEntries(response, itemName, selfContactIds)),
        );
      }),
    );
  }

  /**
   * Saves a questionnaire answer for an item (panel "save on blur"). REAL wire:
   * the app-local `ClientRequestApiService.clientRequestSubmitAnswers` mutation
   * (the same `ClientRequestSubmitAnswers` op the legacy panel uses) — reached
   * here directly, without the legacy state module. Returns void; the state
   * handler reloads detail to reconcile.
   */
  saveItemAnswer(requestId: number, itemId: number, answer: string): Observable<void> {
    return this.clientRequestApi
      .clientRequestSubmitAnswers({
        clientRequestId: requestId,
        clientRequestItemAnswers: [{ clientRequestItemId: itemId, metadata: { answer } }],
      })
      .pipe(map(() => undefined));
  }

  /**
   * Removes an uploaded client file from the request. REAL wire: the app-local
   * `ClientRequestApiService.removeClientRequestFileSubmissions` — a direct
   * `DELETE /client-request/submission-file` REST call (soft-deletes the record,
   * hard-deletes the DMS file), same as the other write endpoints here. Bypasses
   * the `ClientRequestRemoveFile` Hasura action, which is only a pass-through to
   * this endpoint.
   *
   * `fileId` is the FILE id (`CrlItemFile.fileId`) — what the endpoint keys on
   * (`submissionFile.fileId in fileIds`). `itemId` is not needed: the endpoint is
   * request+file scoped, not per item-link.
   */
  removeFileFromItem(requestId: number, itemId: number, fileId: number): Observable<void> {
    return this.withFreshTokenRetry(() =>
      this.clientRequestApi.removeClientRequestFileSubmissions({ clientRequestId: requestId, fileIds: [fileId] }),
    ).pipe(map(() => undefined));
  }

  /**
   * Opens the per-CRL categorization subscription and maps each raw
   * `client_request_submission_file` row into the CXP-local
   * `CrlCategorizationFile` shape (display name stripped of its `_<epoch>` upload
   * suffix). One live query per open request feeds both the uncategorized-files
   * card and the "Categorizing…" toast row state.
   */
  watchRequestSubmissionFiles(requestId: number): Observable<CrlCategorizationFile[]> {
    return this.clientRequestApi
      .subscribeToRequestFileCategorizations(requestId)
      .pipe(map((rows) => rows.map(mapRequestFileCategorizationToCrlFile)));
  }

  /**
   * Opens the per-CRL comments live queries and reduces them to the plain item-id
   * lists behind the row's comment add-on and its unviewed dot. Two subscriptions
   * per open request, each covering every item.
   *
   * Only the unviewed half needs the viewer's contact set — "not written by me"
   * cannot be asked of the server without it. It is read reactively rather than
   * as a snapshot because the ids resolve from the user's email and can land
   * AFTER a direct page hit has already started the subscriptions; `switchMap`
   * reopens that query if the set arrives late or changes. The comment add-on
   * does not wait for any of that: `startWith` lets the combined view emit as
   * soon as the first query answers.
   */
  watchItemsWithComments(requestId: number): Observable<CrlItemCommentIds> {
    const withComments$ = this.clientRequestApi.subscribeToItemsWithComments(requestId);

    const withUnviewed$ = this.store.select(AppState.getUserContactIds).pipe(
      map((ids) => ids ?? []),
      // An empty set would make the server's `_nin` match every contact-authored
      // message, marking the caller's own comments unread. Waiting costs nothing:
      // a caller with no contacts cannot see comments at all.
      filter((ids) => ids.length > 0),
      distinctUntilChanged((a, b) => a.length === b.length && a.every((id, index) => id === b[index])),
      switchMap((ids) => this.clientRequestApi.subscribeToItemsWithUnviewedComments(requestId, ids)),
      startWith([] as RequestItemCommentRef[]),
    );

    return combineLatest([withComments$, withUnviewed$]).pipe(
      map(([withComments, withUnviewed]) => ({
        withComments: toItemIds(withComments),
        withUnviewed: toItemIds(withUnviewed),
        latestMessageIdByItem: toLatestMessageIds(withComments),
      })),
    );
  }

  /**
   * Removes a bulk-uploaded submission file from the request (uncategorized-card
   * "X"). REAL wire: the app-local `ClientRequestApiService.removeClientRequestFiles`
   * (`ClientRequestRemoveFile` mutation — soft-deletes the row + hard-deletes the
   * DMS file). Keys on the FILE id (`CrlCategorizationFile.fileId`), NOT the
   * submission-row id.
   */
  removeSubmissionFile(requestId: number, fileId: number): Observable<void> {
    return this.clientRequestApi
      .removeClientRequestFiles({ clientRequestId: requestId, fileIds: [fileId] })
      .pipe(map(() => undefined));
  }

  /**
   * Marks an item N/A (or undoes it) with an optional note. REAL wire: the
   * app-local `ClientRequestApiService.setClientRequestItemNotApplicable`
   * mutation (`SetClientRequestItemNotApplicable`).
   */
  markItemNotApplicable(itemId: number, isNotApplicable: boolean, note: string | null): Observable<void> {
    return this.clientRequestApi
      .setClientRequestItemNotApplicable({
        clientRequestItemId: itemId,
        isNotApplicable,
        ...(note ? { notApplicableReason: note } : {}),
      })
      .pipe(map(() => undefined));
  }

  /**
   * Per-item "I'm Finished" → move the item to In Review. REAL wire: the
   * app-local REST `POST client-request/section/item/mark-as-finished`
   * (single-item wrap; the endpoint is all-or-nothing over `itemIds`).
   * A `response: false` is surfaced as an error so the state's failure
   * path (optimistic rollback) fires.
   */
  markItemFinished(requestId: number, itemId: number): Observable<void> {
    return this.withFreshTokenRetry(() =>
      this.clientRequestApi.markClientRequestItemsAsFinished(requestId, [itemId]),
    ).pipe(
      map(({ response }) => {
        if (!response) throw new Error(`mark-as-finished rejected for item ${itemId}`);
        return undefined;
      }),
    );
  }

  /**
   * Undo "I'm Finished" → move the item from In Review back to open. REAL wire:
   * the app-local REST `POST client-request/undo-finished` (single-item wrap).
   * The endpoint SILENTLY skips items the caller isn't assigned to (still
   * `response: true`), so the state handler must reload the detail to reconcile
   * rather than trust the boolean; only `response: false` is surfaced as an error.
   */
  undoItemFinished(requestId: number, itemId: number): Observable<void> {
    return this.withFreshTokenRetry(() =>
      this.clientRequestApi.undoClientRequestItemsFinished(requestId, [itemId]),
    ).pipe(
      map(({ response }) => {
        if (!response) throw new Error(`undo-finished rejected for item ${itemId}`);
        return undefined;
      }),
    );
  }

  /**
   * Invites firm contacts (matched by email) to the given items — the "Invite"
   * modal's Send. REAL wire: the app-local REST `POST client-request/section/item/invite`
   * (anduin PR #6789). The backend resolves each assignee email to an existing
   * contact and assigns it (or provisions a secure link server-side). REST-only;
   * `assignees` is an array (today the modal sends exactly one) and `message` is
   * sent through. A `response: false` is surfaced as an error so the dialog reports it.
   */
  inviteToItems(requestId: number, itemIds: number[], assignees: InviteAssignee[], message: string): Observable<void> {
    return this.withFreshTokenRetry(() =>
      this.clientRequestApi.inviteClientRequestItems(requestId, itemIds, assignees, message),
    ).pipe(
      map(({ response }) => {
        if (!response) throw new Error(`invite rejected for request ${requestId}`);
        return undefined;
      }),
    );
  }

  /**
   * Removes one person's access to a single item. The row's own handle decides
   * the array: contacts by `contactId`, guests by `secureLinkId`. `itemIds` is
   * always the one item — an empty array revokes across the WHOLE request.
   *
   * ⚠️ Answers `true` even when nothing matched (out-of-scope ids no-op), so a
   * resolved call is not proof the row is gone — the caller reloads.
   */
  revokeFromItem(requestId: number, itemId: number, target: { contactId: number | null; secureLinkId: number | null }): Observable<void> {
    return this.withFreshTokenRetry(() =>
      this.clientRequestApi.revokeClientRequestItemInvites(
        requestId,
        [itemId],
        target.contactId != null ? [target.contactId] : [],
        target.secureLinkId != null ? [target.secureLinkId] : [],
      ),
    ).pipe(
      map(({ response }) => {
        if (!response) throw new Error(`revoke rejected for request ${requestId}`);
        return undefined;
      }),
    );
  }

  /**
   * Runs a REST call and, if it fails because the Firebase token was stale
   * (`FirebaseAuthException`), refreshes the token and retries it ONCE. The auth
   * rejection happens BEFORE the use case runs, so nothing mutated and the single
   * retry is idempotent-safe. Any other failure passes through unchanged (no blind
   * retry over a call that may have partially executed).
   *
   * Only the REST writes here need this. The GraphQL/Apollo methods
   * (`saveItemAnswer`, `markItemNotApplicable`) are already covered by the Apollo
   * `errorLink`, which refreshes on `invalid-jwt`.
   */
  private withFreshTokenRetry<T>(call: () => Observable<T>): Observable<T> {
    return call().pipe(
      catchError((error: unknown) => {
        if (!isFirebaseAuthError(error)) {
          return throwError(() => error);
        }
        return this.authService.refreshToken().pipe(
          take(1),
          switchMap(() => call()),
        );
      }),
    );
  }

  /**
   * Resolves the user's full set of linked contact ids, mirroring
   * `CxpTasksService`: prefer the cached `AppState` set, fall back to the
   * single contact-record id.
   */
  private resolveContactIds(user: ContactUser): number[] {
    const defaultContactId = user?.getContact()?.id;
    const storedContactIds = this.store.selectSnapshot(AppState.getUserContactIds) ?? [];
    if (storedContactIds.length > 0) return storedContactIds;
    return defaultContactId != null ? [defaultContactId] : [];
  }
}

/** The lazily-loaded per-item files + answer, patched onto `detailData`. */
export interface CrlItemDetailResult {
  files: CrlItemFile[];
  answer: string;
  isAnswerMasked: boolean;
}

/**
 * Pure mapper: the `/page` response -> `CrlRequestDetail`. The request-header
 * fields are read directly off the page so the selector needs no tasks-state
 * join; items carry status/flags/`restricted`/`fileCount` but empty `files` and
 * no `answer` (filled lazily via `getItemDetail`).
 */
function mapCrlPageToCrlDetail(page: CrlPageResponse): CrlRequestDetail {
  const sections = (page.sections ?? []).map(mapPageSection);
  const status = deriveDetailStatus(sections);

  return {
    id: page.clientRequestId,
    clientId: page.clientId,
    name: page.name ?? '',
    description: page.description ?? '',
    clientName: page.clientName ?? null,
    jobId: page.jobId ?? null,
    serviceLine: page.serviceLine ? { id: page.serviceLine.id, name: page.serviceLine.name } : null,
    reviewers: (page.reviewers ?? []).map((reviewer) => ({
      staffId: reviewer.staffId,
      name: reviewer.name,
      email: reviewer.email ?? null,
      isOwner: !!reviewer.isOwner,
    })),
    assignees: (page.assignees ?? []).map((assignee) => ({
      kind: mapAssigneeKind(assignee.type),
      contactId: assignee.contactId ?? null,
      fullName: assignee.fullName,
      email: assignee.email ?? null,
      isOwner: !!assignee.isOwner,
      secureLinkId: assignee.secureLink?.id ?? null,
    })),
    nextDueDate: page.nextDueDate ?? null,
    status,
    statusLabel: CRL_STATUS_LABELS[status],
    submittedAt: page.submittedAt ?? null,
    sections,
  };
}

// Fail-closed: an entry without a type (older backend) is a plain contact.
function mapAssigneeKind(type: CrlApiAssigneeType | undefined): CrlAssigneeKind {
  return type === 'SECURE_LINK' ? 'secureLink' : 'contact';
}

function mapPageSection(section: CrlPageSection): CrlRequestSection {
  return {
    id: section.id,
    name: section.name,
    ordinal: section.ordinal,
    items: (section.items ?? []).map(mapPageItem),
  };
}

function mapPageItem(item: CrlPageItem): CrlRequestItem {
  // The `/page` raw item status/type share the SAME enum values as the legacy
  // `ClientRequestItemStatus` / `RequestItemType`, so the existing derivation +
  // type mapper are reused directly.
  const rawStatus = item.status as unknown as ClientRequestItemStatus;
  const status = deriveItemStatus({ status: rawStatus, dueDate: item.dueDate ?? null, isNotApplicable: !!item.isNotApplicable });

  return {
    id: item.id,
    name: item.name,
    description: item.description ?? '',
    ordinal: item.ordinal,
    type: mapItemType(item.type as unknown as RequestItemType),
    status,
    statusLabel: CRL_STATUS_LABELS[status],
    isEditableStatus: isRawStatusEditable(rawStatus),
    isAwaitingReview: rawStatus === ClientRequestItemStatus.IN_REVIEW,
    isAccepted: rawStatus === ClientRequestItemStatus.DONE,
    dueDate: item.dueDate ?? null,
    isReadyToSubmit: !!item.isReadyToSubmit,
    isNotApplicable: !!item.isNotApplicable,
    notApplicableReason: item.notApplicableReason ?? null,
    flagMessage: item.flagMessage ?? null,
    isRestricted: !!item.restricted,
    // Files + the real answer arrive lazily; masked content is flagged up front
    // so the questionnaire renders read-only before the item detail resolves.
    answer: undefined,
    isAnswerMasked: !!item.isContentMasked,
    assignees: (item.assignees ?? []).map((assignee) => ({
      kind: mapAssigneeKind(assignee.type),
      id: assignee.id ?? null,
      contactId: assignee.contactId ?? null,
      name: assignee.fullName ?? null,
      email: assignee.email ?? null,
      isOwner: !!assignee.isOwner,
      secureLinkId: assignee.secureLink?.id ?? null,
    })),
    fileCount: item.fileCount ?? 0,
    files: [],
  };
}

/**
 * Pure mapper: the `/item/detail` response -> the lazily-patched files + answer.
 * `files[]` are client uploads (`isReference:false`), active `fileReferences[]`
 * are firm reference files (`isReference:true`). The answer is masked-aware.
 */
function mapCrlItemDetail(detail: CrlItemDetailResponse): CrlItemDetailResult {
  return {
    files: mapItemDetailFiles(detail),
    ...resolveItemDetailAnswer(detail),
  };
}

function mapItemDetailFiles(detail: CrlItemDetailResponse): CrlItemFile[] {
  const fromSubmissions: CrlItemFile[] = (detail.files ?? []).map((file) => ({
    id: file.id ?? file.fileId ?? 0,
    fileId: file.fileId ?? file.id ?? 0,
    // Client uploads are stored under a timestamped name; strip it for display.
    name: getOriginalBoxFileName(file.name ?? ''),
    isFlagged: !!file.isFlagged,
    createdAt: file.createdAt ?? null,
    size: file.size ?? null,
    dmsId: file.dmsFileId ?? file.dmsId ?? null,
    isReference: false,
  }));

  const fromReferences: CrlItemFile[] = (detail.fileReferences ?? [])
    .filter((reference) => reference.active)
    .map((reference) => ({
      id: reference.id ?? reference.fileId ?? 0,
      fileId: reference.fileId ?? reference.id ?? 0,
      name: reference.name ?? '',
      isFlagged: false,
      createdAt: reference.createdAt ?? null,
      size: reference.size ?? null,
      dmsId: reference.dmsId ?? null,
      isReference: true,
    }));

  return [...fromSubmissions, ...fromReferences];
}

/**
 * Masked-aware answer resolution: a null answer on masked content renders the
 * masked marker read-only; otherwise the decrypted answer, then ''.
 */
function resolveItemDetailAnswer(detail: CrlItemDetailResponse): Pick<CrlItemDetailResult, 'answer' | 'isAnswerMasked'> {
  if (detail.answer == null && detail.isContentMasked) {
    return { answer: MASKED_ITEM_RESPONSE_VALUE, isAnswerMasked: true };
  }
  return { answer: detail.answer?.answer ?? '', isAnswerMasked: false };
}

/**
 * Pure mapper: a raw categorization subscription row -> `CrlCategorizationFile`.
 * The stored file name is timestamped (`_<epoch>`); strip it for display, matching
 * the Documents-tab flow. `isLinked` reflects whether the file has been
 * categorized into any request item yet.
 */
function mapRequestFileCategorizationToCrlFile(row: RequestFileCategorization): CrlCategorizationFile {
  return {
    id: row.id,
    fileId: row.fileId,
    name: getOriginalBoxFileName(row.file?.name ?? ''),
    size: row.file?.fileInfo?.size ?? null,
    dmsId: row.file?.dmsId ?? row.file?.dmsFileId ?? null,
    status: mapSubmissionFileStatus(row.status),
    isLinked: (row.itemSubmissionFiles?.length ?? 0) > 0,
  };
}

/**
 * Pure mapper: the comments subscription's two aliased blocks -> item-id lists.
 * `message.entity_id` is a `bigint`, so an id can arrive as a numeric string;
 * each row is coerced, and anything that does not resolve to a real item id is
 * dropped rather than emitted as a `NaN`/`0` that matches no row.
 */
/**
 * Each item's newest message id, from the has-comments rows. Rows without a
 * usable pair are dropped rather than recorded as `NaN`, which would read as a
 * change on every emission and reload the open thread forever.
 */
function toLatestMessageIds(rows: RequestItemCommentRef[] | null | undefined): Record<number, number> {
  const latest: Record<number, number> = {};
  for (const row of rows ?? []) {
    const itemId = Number(row?.entityId);
    const messageId = Number(row?.id);
    if (Number.isFinite(itemId) && itemId > 0 && Number.isFinite(messageId) && messageId > 0) {
      latest[itemId] = messageId;
    }
  }
  return latest;
}

function toItemIds(rows: RequestItemCommentRef[] | null | undefined): number[] {
  return (rows ?? []).map((row) => Number(row?.entityId)).filter((id) => Number.isFinite(id) && id > 0);
}

/**
 * Translates the API categorization status into the CXP-local status (the
 * isolation seam). `AUTOMATICALLY_CATEGORIZED` collapses into `CATEGORIZED`; any
 * unexpected value degrades to `UPLOADING` (still in-flight, never surfaced as a
 * settled uncategorized file).
 */
function mapSubmissionFileStatus(status: RequestSubmissionFileStatus): CrlCategorizationStatus {
  switch (status) {
    case RequestSubmissionFileStatus.CATEGORIZING:
      return CrlCategorizationStatus.CATEGORIZING;
    case RequestSubmissionFileStatus.CATEGORIZED:
    case RequestSubmissionFileStatus.AUTO_CATEGORIZED:
      return CrlCategorizationStatus.CATEGORIZED;
    case RequestSubmissionFileStatus.UNCATEGORIZED:
      return CrlCategorizationStatus.UNCATEGORIZED;
    case RequestSubmissionFileStatus.UPLOADING:
    default:
      return CrlCategorizationStatus.UPLOADING;
  }
}

/**
 * True when a failed REST call was rejected because the Firebase token was
 * stale/expired. ArtEngine's error handler surfaces the exception name at
 * `extensions.details.reason` (here `FirebaseAuthException`) on a 400.
 */
function isFirebaseAuthError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  const reason = (error.error as { extensions?: { details?: { reason?: string } } } | null)?.extensions?.details?.reason;
  return reason === 'FirebaseAuthException';
}

function mapItemType(type: RequestItemType): CrlItemType {
  switch (type) {
    case RequestItemType.DOCUMENT_REQUEST:
      return CrlItemType.DOCUMENT_REQUEST;
    case RequestItemType.E_SIGN:
      return CrlItemType.E_SIGN;
    case RequestItemType.QUESTIONNAIRE:
    default:
      return CrlItemType.QUESTIONNAIRE;
  }
}

/**
 * Maps a raw item status to the normalized CRL status. `is_not_applicable`
 * wins over the stored status; overdue is derived from a past due date on a
 * still-open item.
 */
function deriveItemStatus(item: { status: ClientRequestItemStatus; dueDate: string | null | undefined; isNotApplicable: boolean }): CrlItemStatus {
  if (item.isNotApplicable) return CrlItemStatus.NOT_APPLICABLE;

  const base = mapRawStatus(item.status);
  if (base === CrlItemStatus.WAITING && isOverdue(item.dueDate)) {
    return CrlItemStatus.OVERDUE;
  }
  return base;
}

/**
 * Raw (database) statuses in which the client may still modify an item — upload /
 * replace / remove files or edit an answer. Only "open" (`OPEN`/`TO_DO`, where a
 * past-due "overdue" item still sits) and "changes requested" (`REJECTED`)
 * qualify; `IN_REVIEW` and `DONE` are locked. Keyed on the raw status (not the
 * derived display status) so an overdue item — still `OPEN` in the DB — stays
 * editable without special-casing.
 */
const CRL_EDITABLE_RAW_STATUSES: ReadonlySet<ClientRequestItemStatus> = new Set([
  ClientRequestItemStatus.OPEN,
  ClientRequestItemStatus.TO_DO,
  ClientRequestItemStatus.REJECTED,
]);

function isRawStatusEditable(status: ClientRequestItemStatus): boolean {
  return CRL_EDITABLE_RAW_STATUSES.has(status);
}

function mapRawStatus(status: ClientRequestItemStatus): CrlItemStatus {
  switch (status) {
    case ClientRequestItemStatus.TO_DO:
    case ClientRequestItemStatus.OPEN:
      return CrlItemStatus.WAITING;
    // The one place the read path resolves the In Review concept: submitted
    // items display as FINISHED unless CRL_IN_REVIEW_ENABLED surfaces the
    // intermediate state. Undo is unaffected — it reads `isAwaitingReview`,
    // derived from this same raw status above.
    case ClientRequestItemStatus.IN_REVIEW:
      return CRL_SUBMITTED_ITEM_STATUS;
    case ClientRequestItemStatus.REJECTED:
      return CrlItemStatus.NEEDS_UPDATE;
    case ClientRequestItemStatus.DONE:
      return CrlItemStatus.FINISHED;
    default:
      return CrlItemStatus.WAITING;
  }
}

function isOverdue(dueDate: string | null | undefined): boolean {
  if (!dueDate) return false;
  const due = Date.parse(dueDate);
  return !isNaN(due) && due < Date.now();
}

/**
 * Rolls item statuses up to a single request-level status: anything needing
 * update surfaces first, then in-review, then "all finished", otherwise
 * waiting. N/A items don't count toward the roll-up.
 */
function deriveDetailStatus(sections: CrlRequestSection[]): CrlItemStatus {
  const items = sections.flatMap((section) => section.items).filter((item) => !item.isNotApplicable);
  if (items.length === 0) return CrlItemStatus.FINISHED;

  if (items.some((item) => item.status === CrlItemStatus.NEEDS_UPDATE)) return CrlItemStatus.NEEDS_UPDATE;
  if (items.some((item) => item.status === CrlItemStatus.OVERDUE)) return CrlItemStatus.OVERDUE;
  // Only reachable while CRL_IN_REVIEW_ENABLED surfaces the concept; otherwise
  // no item resolves to IN_REVIEW and a fully-submitted request rolls up to
  // FINISHED below.
  if (items.some((item) => item.status === CrlItemStatus.IN_REVIEW)) return CrlItemStatus.IN_REVIEW;
  // Completion goes through the shared done-bucket (never an individual enum
  // member) so this roll-up can't drift from the tiles/section split.
  if (items.every((item) => CRL_DONE_STATUSES.has(item.status))) return CrlItemStatus.FINISHED;
  return CrlItemStatus.WAITING;
}

/**
 * The fully-resolved client-facing presentation of one activity row: the action
 * text, the linked object (+ its colour kind), the file chips to render, and
 * whether the comment box is the critical red variant. Every field is derived
 * from `(eventType, data, itemName, files)` by `resolveActivityPresentation` so
 * a single place owns per-event wording, linking, and chip rules.
 *
 * The client feed MUST NOT use status language (see
 * `research/05-activity-endpoint-wiring.md`). `linkedObjectKind` colours the
 * linked object — `item` = plain dark text (item name / person name), `file` =
 * a live file name in teal, `file-deleted` = a removed file name, muted. Note
 * that colour (`linkedObjectKind`) is INDEPENDENT of chips (`files`): a flagged
 * file is a teal link with NO chip; a removed file is a muted link with no chip.
 */
interface ActivityPresentation {
  action: string;
  linkedObject: string | null;
  linkedObjectKind: CrlActivityLinkKind | null;
  files: CrlActivityFile[];
  isCritical: boolean;
}

/**
 * Static phrasing for the events whose action + link is fixed (no `data`-derived
 * wording). `linkKind: 'item'` appends the selected item name in plain dark text;
 * `critical` renders the reason in the red "requested changes" box. Events with
 * `data`-derived action/linked-object (file + reviewer + reassignment ops) are
 * handled explicitly in `resolveActivityPresentation` and are NOT listed here.
 */
interface StaticPhrasing {
  action: string;
  linkKind?: 'item';
  critical?: boolean;
}

const STATIC_PHRASING: Record<string, StaticPhrasing> = {
  SENT: { action: 'sent', linkKind: 'item' },
  APPROVED: { action: 'accepted', linkKind: 'item' },
  REJECTED: { action: 'requested changes', critical: true },
  MARK_FINISHED: { action: 'marked as finished.' },
  UNDO: { action: 'unmarked as finished.' },
  MARK_NA: { action: 'marked as not applicable.' },
  COMMENT_ADDED: { action: 'left a comment' },
};

/**
 * The ONLY `data` keys that carry a comment/reason box in the real payload
 * (confirmed by the backend example): `REJECTED.notes` (critical), `MARK_NA.note`
 * (normal), `COMMENT_ADDED.comment` (normal). Order matters — first match wins.
 */
const COMMENT_DATA_KEYS = ['notes', 'note', 'comment'];

/**
 * localStorage key for the dev-only activity-mock payload. Devs set it (in
 * DevTools) to a `{ activities: [...] }` object or a bare `[...]` array of raw
 * `ClientRequestItemActivity` rows; clear it with
 * `localStorage.removeItem('crl-activity-mock')`.
 */
const CRL_ACTIVITY_MOCK_STORAGE_KEY = 'crl-activity-mock';

/** Firebase project id on which the dev-only activity-mock injection is enabled. */
const DEV_ACTIVITY_MOCK_PROJECT_ID = 'dev-mvp';

/**
 * PERMANENT dev-only tool (NOT a remove-before-merge hack). Lets a dev preview an
 * arbitrary CRL activity feed on dev/test/isolated environments — which need the
 * event engine running to emit real events — without redeploying, by dropping a
 * mock payload into `localStorage['crl-activity-mock']`.
 *
 * DOUBLE-GATED so it can safely ship: it only runs when the runtime firebase
 * project is `dev-mvp` AND the opt-in localStorage key is present. On any other
 * project — or with no key — it is a complete no-op and returns `response`
 * untouched (including `null`).
 *
 * When active it merges the mock activities into the real feed and re-sorts
 * newest-first: an empty real feed therefore becomes "just the mocks" (replace),
 * a non-empty feed becomes real+mocks combined — one code path covers both.
 * Everything is parsed DEFENSIVELY: malformed JSON / non-array payloads log a
 * warning and no-op rather than throw.
 */
function injectDevActivityMocks(
  response: ClientRequestItemActivityResponse | null,
  projectId: string | null | undefined,
): ClientRequestItemActivityResponse | null {
  if (projectId !== DEV_ACTIVITY_MOCK_PROJECT_ID) return response;

  const raw = readActivityMockStorage();
  if (raw === null) return response;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (error) {
    console.warn('[CRL] Ignoring malformed `crl-activity-mock` localStorage value — not valid JSON.', error);
    return response;
  }

  const mockActivities = extractMockActivities(parsed);
  if (mockActivities.length === 0) return response;

  const resolvedMocks: ClientRequestItemActivity[] = mockActivities.map((activity) => ({
    ...(activity as unknown as ClientRequestItemActivity),
    // Resolve the mock timestamp at READ time so relative-time rendering stays fresh.
    timestamp: resolveMockTimestamp(activity['timestamp']),
  }));

  const realActivities = response?.activities ?? [];
  const combined = [...realActivities, ...resolvedMocks].sort(byTimestampDesc);

  console.warn(
    `[CRL] Injecting ${resolvedMocks.length} mock activities from localStorage (dev-mvp only) — clear with localStorage.removeItem('crl-activity-mock')`,
  );

  return {
    requestItemId: response?.requestItemId ?? 0,
    limit: response?.limit ?? 100,
    offset: response?.offset ?? 0,
    activities: combined,
    count: combined.length,
  };
}

/**
 * Resolves a mock activity's `timestamp` field at read time:
 * - `number` → an offset in MINUTES from now (`negative` = past, `0` = now,
 *   `positive` = future), rendered as an absolute ISO string;
 * - `string` → used as-is (absolute ISO passthrough);
 * - anything else → now.
 */
function resolveMockTimestamp(value: unknown): string {
  if (typeof value === 'number') return new Date(Date.now() + value * 60000).toISOString();
  if (typeof value === 'string') return value;
  return new Date().toISOString();
}

/** Reads the opt-in localStorage payload; returns null when absent/empty or unavailable. */
function readActivityMockStorage(): string | null {
  try {
    const value = localStorage.getItem(CRL_ACTIVITY_MOCK_STORAGE_KEY);
    return value && value.trim() ? value : null;
  } catch {
    // `localStorage` can throw in locked-down contexts — treat as absent.
    return null;
  }
}

/** Accepts `{ activities: [...] }` OR a bare `[...]`; keeps only object entries. */
function extractMockActivities(parsed: unknown): Record<string, unknown>[] {
  const list = Array.isArray(parsed)
    ? parsed
    : isPlainObject(parsed) && Array.isArray(parsed['activities'])
      ? parsed['activities']
      : [];
  return list.filter((entry): entry is Record<string, unknown> => isPlainObject(entry));
}

/** Newest-first comparator by ISO timestamp; unparseable timestamps sort last. */
function byTimestampDesc(a: ClientRequestItemActivity, b: ClientRequestItemActivity): number {
  const ta = a.timestamp ? Date.parse(a.timestamp) : NaN;
  const tb = b.timestamp ? Date.parse(b.timestamp) : NaN;
  return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
}

/**
 * Pure mapper: the per-item activity response → the feed view model.
 * `itemName` (the selected item's name, known at the state layer) supplies the
 * teal linked object for SENT/APPROVED. `selfContactIds` drives best-effort
 * "You" detection. Everything is parsed DEFENSIVELY — `data` shape is not
 * guaranteed and `referenceFiles` is empty today.
 */
export function mapItemActivityToEntries(
  response: ClientRequestItemActivityResponse | null | undefined,
  itemName: string | null,
  selfContactIds: number[] = [],
): CrlActivityEntry[] {
  const activities = response?.activities ?? [];
  return activities.map((activity, index) => mapActivityEntry(activity, index, itemName, selfContactIds));
}

function mapActivityEntry(
  activity: ClientRequestItemActivity,
  index: number,
  itemName: string | null,
  selfContactIds: number[],
): CrlActivityEntry {
  const actor = activity?.actor ?? null;
  const eventType = (activity?.eventType ?? '').toString();
  const isSelf = isSelfActor(actor, selfContactIds);

  // Canonical file set (referenceFiles first, deduped) — the presentation
  // resolver decides which of these become chips per event type.
  const allFiles = collectActivityFiles(activity);
  const presentation = resolveActivityPresentation(eventType, activity?.data, itemName, allFiles);
  const comment = resolveActivityComment(activity, presentation.isCritical);

  return {
    id: `${index}-${activity?.timestamp ?? ''}-${eventType}`,
    eventType,
    actorName: resolveActorName(actor, isSelf),
    actorInitials: resolveActorInitials(actor),
    avatarColorKey: pickAvatarColor(actor),
    action: presentation.action,
    linkedObject: presentation.linkedObject,
    // Guard the kind so a null linked object never carries a stray colour class.
    linkedObjectKind: presentation.linkedObject ? presentation.linkedObjectKind : null,
    files: presentation.files,
    comment,
    timestamp: activity?.timestamp ?? null,
    isSelf,
    // Only an explicit `true` masks — an absent/garbage flag must never make a
    // normal row inert.
    isMasked: activity?.isMasked === true,
  };
}

/**
 * Computes the full client-facing presentation for one event from its
 * `(eventType, data, itemName, files)`. This is where per-event wording, the
 * linked object + its colour, and the chip set diverge:
 *
 * - Static events (SENT/APPROVED/REJECTED/MARK_FINISHED/UNDO/MARK_NA/COMMENT_ADDED)
 *   use {@link STATIC_PHRASING}; only item-linked ones append the item name.
 * - File events derive action/link/chips from `data` + `referenceFiles`:
 *   uploaded (teal link only for a single file; chips for all), replaced (old
 *   name baked into the action, new name the teal link, ONLY the new file
 *   chipped), flagged (teal link, NO chip), removed (muted link, no chip).
 * - Reviewer/reassignment events derive a dark linked name from `data`.
 * - Anything unmapped degrades to a humanized enum name with no link/chips.
 */
function resolveActivityPresentation(
  eventType: string,
  data: unknown,
  itemName: string | null,
  allFiles: CrlActivityFile[],
): ActivityPresentation {
  const record = isPlainObject(data) ? data : null;
  const base: ActivityPresentation = { action: '', linkedObject: null, linkedObjectKind: null, files: [], isCritical: false };

  switch (eventType) {
    case 'FILE_UPLOADED': {
      // Teal inline link ONLY when a single file was uploaded; otherwise no
      // inline link and every file is chipped.
      const single = allFiles.length === 1 ? allFiles[0] : null;
      return {
        ...base,
        action: 'uploaded',
        linkedObject: single?.name ?? null,
        linkedObjectKind: single ? 'file' : null,
        files: allFiles,
      };
    }
    case 'FILE_REPLACED': {
      // referenceFiles carries BOTH files, so read the pair from `data` instead:
      // old name is plain text baked into the action, new name is the teal link,
      // and ONLY the new file is chipped (never both).
      const oldFile = readFileObject(record?.['file_old']);
      const newFile = readFileObject(record?.['file_new']);
      const oldName = oldFile?.name ?? null;
      return {
        ...base,
        action: oldName ? `replaced ${oldName} with` : 'replaced',
        linkedObject: newFile?.name ?? null,
        linkedObjectKind: newFile ? 'file' : null,
        files: newFile ? [newFile] : [],
      };
    }
    case 'FILE_FLAGGED': {
      // Teal link, but deliberately NO chip.
      const file = allFiles[0] ?? null;
      return { ...base, action: 'flagged', linkedObject: file?.name ?? null, linkedObjectKind: file ? 'file' : null };
    }
    case 'FILE_REMOVED': {
      // Muted link (removed file can't be previewed/downloaded), no chip.
      const file = allFiles[0] ?? null;
      return { ...base, action: 'removed', linkedObject: file?.name ?? null, linkedObjectKind: file ? 'file-deleted' : null };
    }
    case 'REVIEWER_CHANGED': {
      const name = record ? firstString(record, ['name_new']) : null;
      return { ...base, action: 'changed the reviewer to', linkedObject: name, linkedObjectKind: name ? 'item' : null };
    }
    case 'REQUEST_REASSIGNED': {
      const name = record ? firstString(record, ['name']) : null;
      return { ...base, action: 'reassigned the request to', linkedObject: name, linkedObjectKind: name ? 'item' : null };
    }
    default: {
      const phrasing = STATIC_PHRASING[eventType];
      if (!phrasing) return { ...base, action: humanizeEventType(eventType) };
      const linkedObject = phrasing.linkKind === 'item' ? itemName?.trim() || null : null;
      return {
        ...base,
        action: phrasing.action,
        linkedObject,
        linkedObjectKind: linkedObject ? 'item' : null,
        isCritical: !!phrasing.critical,
      };
    }
  }
}

/**
 * Best-effort self detection: a client user is a contact, so we only treat an
 * actor as "You" when it is NOT a firm staff actor and its id matches one of the
 * caller's linked contact ids. Staff ids live in a different namespace, so we
 * never mistake a firm event for the client's own.
 */
function isSelfActor(actor: ClientRequestItemActivityActor | null, selfContactIds: number[]): boolean {
  // `id: 0` is the system actor and masked fields arrive as empty strings, not
  // null — a falsy id can never be "You".
  if (!actor || !actor.id) return false;
  const type = (actor.type ?? '').toString().toLowerCase();
  if (type === 'staff') return false;
  return selfContactIds.includes(actor.id);
}

function resolveActorName(actor: ClientRequestItemActivityActor | null, isSelf: boolean): string {
  if (isSelf) return 'You';
  const full = actor?.fullName?.trim();
  if (full) return full;
  const composed = [actor?.name, actor?.lastName].map((part) => part?.trim()).filter(Boolean).join(' ');
  return composed || actor?.name?.trim() || 'Someone';
}

function resolveActorInitials(actor: ClientRequestItemActivityActor | null): string {
  const composed = [actor?.name, actor?.lastName].map((part) => part?.trim()).filter(Boolean).join(' ');
  const nameForInitials = composed || actor?.fullName?.trim() || null;
  // Masked/system emails arrive as empty strings — normalise to null so the
  // initials util never derives an initial from `''`.
  return getCxpContactInitials(nameForInitials, actor?.email?.trim() || null);
}

/**
 * Deterministic avatar colour: same actor → same pastel. Seeded from the actor
 * id when present (stable across name masking), otherwise the display name.
 */
function pickAvatarColor(actor: ClientRequestItemActivityActor | null): CrlAvatarColorKey {
  const seed = actor?.id != null ? `#${actor.id}` : (actor?.fullName || actor?.name || actor?.email || '?');
  return pickAvatarColorKey(seed);
}

/** `MARK_FINISHED` → `mark finished`; a safe fallback for any unmapped op. */
function humanizeEventType(eventType: string): string {
  if (!eventType) return 'updated this item';
  return eventType.toLowerCase().replace(/_/g, ' ');
}

/**
 * The canonical file set for an activity. `referenceFiles` (camelCase) is the
 * source of truth; the snake_case `data.file_list` is ONLY a fallback for when
 * `referenceFiles` is empty. The real payload now sends BOTH, so concatenating
 * them would render every file twice — we pick one source and dedupe by id
 * defensively on top.
 */
function collectActivityFiles(activity: ClientRequestItemActivity): CrlActivityFile[] {
  const fromReferences: CrlActivityFile[] = (activity?.referenceFiles ?? [])
    .filter((file): file is NonNullable<typeof file> => !!file)
    .map((file) => ({
      id: file.id ?? 0,
      externalId: file.externalId ?? null,
      name: file.name?.trim() || 'File',
      size: file.fileSize ?? null,
    }));

  const source = fromReferences.length > 0 ? fromReferences : readFileList(activity?.data);
  return dedupeFilesById(source);
}

/** Drops later duplicates that share a (non-zero) id; keeps unknown-id (0) entries. */
function dedupeFilesById(files: CrlActivityFile[]): CrlActivityFile[] {
  const seen = new Set<number>();
  return files.filter((file) => {
    if (!file.id) return true;
    if (seen.has(file.id)) return false;
    seen.add(file.id);
    return true;
  });
}

/** Defensively pulls file-shaped entries out of a `{ file_list: [...] }` group. */
function readFileList(data: unknown): CrlActivityFile[] {
  const record = isPlainObject(data) ? data : null;
  const list = record?.['file_list'];
  if (!Array.isArray(list)) return [];

  return list.map(readFileObject).filter((file): file is CrlActivityFile => file !== null);
}

/**
 * Defensively reads a single file object (snake_case OR camelCase — the real
 * payload sends `file_list` entries and `file_old`/`file_new` in snake_case).
 * Returns null when there is no usable name.
 */
function readFileObject(value: unknown): CrlActivityFile | null {
  if (!isPlainObject(value)) return null;
  const name = firstString(value, ['name', 'fileName', 'file_name']);
  if (!name) return null;
  return {
    id: firstNumber(value, ['id', 'fileId', 'file_id']) ?? 0,
    externalId: firstString(value, ['externalId', 'external_id', 'dmsId', 'dms_id', 'externalDmsFileId']),
    // Submission files are stored under the upload-timestamped name; strip it so
    // the feed shows the same clean name as the Documents tab (mapItemFiles).
    name: getOriginalBoxFileName(name),
    size: firstNumber(value, ['fileSize', 'file_size', 'size']),
  };
}

/**
 * Optional comment/reason box. `isCritical` (decided by the presentation, i.e.
 * REJECTED) renders the red "requested changes" box; otherwise a normal box.
 * Only surfaces when `data` carries one of the confirmed comment keys
 * (`notes` / `note` / `comment`).
 */
function resolveActivityComment(activity: ClientRequestItemActivity, isCritical: boolean): CrlActivityComment | null {
  const record = isPlainObject(activity?.data) ? activity.data : null;
  const text = record ? firstString(record, COMMENT_DATA_KEYS) : null;
  if (!text) return null;
  return { text, isCritical };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function firstString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

function firstNumber(record: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && !isNaN(value)) return value;
    if (typeof value === 'string' && value.trim() && !isNaN(Number(value))) return Number(value);
  }
  return null;
}
